import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { User, Transaction, UserRole } from '../types';
import { useLanguage } from './LanguageContext';
import { db, auth, firebase, functions } from '../services/firebase';
import { initializePayment } from '../services/paymentService';
import { httpsCallable } from 'firebase/functions';

// UPDATED: Stores Plan ID instead of raw amounts
interface PendingTransaction {
    orderId: string;
    planId: string; 
    type: 'subscription' | 'addon';
    timestamp: number;
}

interface AuthContextType {
    user: User | null;
    login: (response: any) => void;
    loginWithEmail: (email: string, pass: string) => Promise<void>;
    registerWithEmail: (name: string, email: string, pass: string, role?: UserRole) => Promise<void>;
    resendVerification: (email: string, pass: string) => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    loginWithGoogle: (role?: UserRole) => Promise<void>;
    logout: () => void;
    consumeSwarna: (amount: number, toolId: string) => void;
    refundSwarna: (amount: number, toolId: string, reason?: string) => void;
    // UPDATED: Accepts PlanID only
    buyCredits: (planId: string, type: 'subscription' | 'addon') => Promise<void>;
    cancelSubscription: () => Promise<void>;
    resumeSubscription: () => Promise<void>;
    checkPaymentStatus: () => Promise<boolean>;
    transactions: Transaction[];
    authError: string | null;
    setAuthError: (error: string | null) => void;
    isInitialized: boolean;
    isGoogleClientConfigured: boolean;
    canGenerate: () => boolean;
    isCloudConnected: boolean;
    pendingTx: PendingTransaction | null;
    clearPendingOrder: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const FREE_STARTING_SWARNAS = 150;
const PENDING_TX_KEY = 'najeweller_pending_tx_v3'; // Bumped version to clear old cache

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useLanguage();
    const [user, setUser] = useState<User | null>(null);
    const userRefState = useRef<User | null>(null);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [authError, setAuthError] = useState<string | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isCloudConnected, setIsCloudConnected] = useState(false);
    
    // Initialize pendingTx from local storage
    const [pendingTx, setPendingTx] = useState<PendingTransaction | null>(() => {
        const stored = localStorage.getItem(PENDING_TX_KEY);
        try {
            return stored ? JSON.parse(stored) : null;
        } catch { return null; }
    });

    const isGoogleClientConfigured = !!process.env.GOOGLE_CLIENT_ID;

    useEffect(() => {
        userRefState.current = user;
    }, [user]);

    // Keep LocalStorage in sync
    useEffect(() => {
        if (pendingTx) {
            localStorage.setItem(PENDING_TX_KEY, JSON.stringify(pendingTx));
        } else {
            localStorage.removeItem(PENDING_TX_KEY);
        }
    }, [pendingTx]);

    const checkSubscriptionStatus = async (userData: User): Promise<User> => {
        if (userData.plan === 'basic' || !userData.subscriptionEndDate) return userData;
        const now = new Date();
        const endDate = new Date(userData.subscriptionEndDate);
        if (now > endDate) {
            return {
                ...userData,
                plan: 'basic',
                subscriptionEndDate: undefined,
                cancelAtPeriodEnd: undefined
            };
        }
        return userData;
    };

    // Helper to create user doc if missing
    const ensureUserDoc = async (uid: string, authUser: any, role: UserRole = 'shopper') => {
        const userRef = db.collection("users").doc(uid);
        const doc = await userRef.get();
        if (!doc.exists) {
            const newUser: User = {
                id: uid,
                email: authUser.email,
                name: authUser.displayName || "User",
                picture: authUser.photoURL || `https://api.dicebear.com/8.x/initials/svg?seed=${uid}`,
                phoneNumber: authUser.phoneNumber || null,
                role: role, 
                swarna: FREE_STARTING_SWARNAS,
                swarnas: FREE_STARTING_SWARNAS,
                plan: 'basic',
                monthlyGenerationCount: 0,
                dailyGenerations: 0,
                mockupCount: 0,
                transactions: [],
                createdAt: new Date().toISOString(),
                lastGenerationDate: new Date().toISOString().split('T')[0]
            };
            await userRef.set(newUser);
        }
    };

    useEffect(() => {
        const initAuth = async () => {
            const safetyTimer = setTimeout(() => !isInitialized && setIsInitialized(true), 4000);
            let unsubscribeUser: any = null;
            let unsubscribeHistory: any = null;
            
            if (auth) {
                auth.onAuthStateChanged(async (firebaseUser: any) => {
                    if (firebaseUser) {
                        const uid = firebaseUser.uid;
                        if (db) {
                            try {
                                const userRef = db.collection("users").doc(uid);
                                
                                // 1. Real-time User Data
                                unsubscribeUser = userRef.onSnapshot(async (doc: any) => {
                                    if (doc.exists) {
                                        const d = doc.data();
                                        const swarnaVal = typeof d.swarna === 'number' ? d.swarna : (d.swarnas || 0);
                                        const safeSwarnas = Math.floor(Number(swarnaVal));
                                        
                                        let userData: User = { 
                                            ...d, 
                                            swarnas: isNaN(safeSwarnas) ? 0 : safeSwarnas,
                                            transactions: [] 
                                        };
                                        
                                        userData = await checkSubscriptionStatus(userData);
                                        setUser(userData);
                                        setIsCloudConnected(true);
                                    } else {
                                        await ensureUserDoc(uid, firebaseUser);
                                    }
                                });

                                // 2. Real-time History
                                unsubscribeHistory = userRef.collection("history")
                                    .orderBy("timestamp", "desc")
                                    .limit(50) 
                                    .onSnapshot((snapshot: any) => {
                                        const historyData = snapshot.docs.map((doc: any) => {
                                            const data = doc.data();
                                            let displayDate = data.date;
                                            
                                            // Convert to IST if timestamp is available
                                            if (data.timestamp && typeof data.timestamp.toDate === 'function') {
                                                try {
                                                    displayDate = data.timestamp.toDate().toLocaleString('en-IN', {
                                                        timeZone: 'Asia/Kolkata',
                                                        day: 'numeric',
                                                        month: 'short',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: true
                                                    });
                                                } catch (e) {
                                                    console.error("Error formatting date", e);
                                                }
                                            }

                                            return {
                                                ...data,
                                                id: doc.id,
                                                date: displayDate
                                            };
                                        }) as Transaction[];
                                        setTransactions(historyData);
                                    });

                            } catch (e) { 
                                console.error(e);
                                setIsCloudConnected(false); 
                            }
                        }
                    } else {
                        setUser(null);
                        setTransactions([]);
                        if (unsubscribeUser) unsubscribeUser();
                        if (unsubscribeHistory) unsubscribeHistory();
                    }
                    clearTimeout(safetyTimer);
                    setIsInitialized(true);
                });
            } else {
                setIsInitialized(true);
            }
        };
        initAuth();
    }, []);

    const loginWithEmail = async (e: string, p: string) => { if(auth) await auth.signInWithEmailAndPassword(e, p); };
    
    const registerWithEmail = async (n: string, e: string, p: string, role: UserRole = 'shopper') => {
        if(!auth) return;
        const uc = await auth.createUserWithEmailAndPassword(e, p);
        if(uc.user) {
            await uc.user.updateProfile({ displayName: n, photoURL: `https://api.dicebear.com/8.x/initials/svg?seed=${n}` });
            await uc.user.sendEmailVerification();
            
            const newUser: User = {
                id: uc.user.uid, 
                email: e, 
                name: n, 
                picture: `https://api.dicebear.com/8.x/initials/svg?seed=${n}`,
                phoneNumber: null,
                role: role, 
                swarna: FREE_STARTING_SWARNAS, 
                swarnas: FREE_STARTING_SWARNAS,
                plan: 'basic', 
                monthlyGenerationCount: 0, 
                dailyGenerations: 0, 
                mockupCount: 0, 
                transactions: [],
                lastGenerationDate: new Date().toISOString().split('T')[0],
                createdAt: new Date().toISOString()
            };
            await db.collection("users").doc(uc.user.uid).set(newUser);
            await auth.signOut();
        }
    };

    const loginWithGoogle = async (role: UserRole = 'shopper') => { 
        if(!auth) return;
        try {
            const result = await auth.signInWithPopup(new firebase.auth.GoogleAuthProvider());
            if (result.user) {
                await ensureUserDoc(result.user.uid, result.user, role);
            }
        } catch (error) {
            console.error("Google Login Error", error);
            throw error;
        }
    };

    const logout = async () => { if(auth) await auth.signOut(); setUser(null); };
    const login = (r: any) => { console.log(r); }; 
    const resendVerification = async (e: string, p: string) => { if(auth) (await auth.signInWithEmailAndPassword(e, p)).user?.sendEmailVerification(); };
    const resetPassword = async (e: string) => { if(auth) await auth.sendPasswordResetEmail(e); };
    
    const consumeSwarna = useCallback((amount: number, toolId: string) => {
        const currentUser = userRefState.current;
        if (!currentUser) throw new Error("No user logged in");
        const currentSwarnas = Math.floor(Number(currentUser.swarnas));
        
        if (currentUser.plan === 'basic') {
            if (toolId === 'mockup_generator') {
                if ((currentUser.mockupCount || 0) >= 3) {
                    throw new Error("Free Mockup limit (3/month) reached. Please upgrade to a Plan.");
                }
            } else {
                if ((currentUser.monthlyGenerationCount || 0) >= 10) {
                    throw new Error(t('errorMonthlyLimit'));
                }
            }
        }

        if (currentSwarnas < amount) {
            throw new Error(t('errorInsufficientSwarnas').replace('{required}', amount.toString()).replace('{available}', currentSwarnas.toString()));
        }

        setUser(prev => prev ? ({ ...prev, swarnas: prev.swarnas - amount }) : null);
    }, [t]);

    const refundSwarna = useCallback((amount: number, toolId: string, reason: string = "Generation Failed") => {
        console.log("Processing refund:", reason);
        setUser(prev => prev ? ({ ...prev, swarnas: prev.swarnas + amount }) : null);
    }, []);

    // --- NEW: Buy Credits with Plan ID ---
    const buyCredits = useCallback(async (planId: string, type: 'subscription' | 'addon') => {
        const currentUser = userRefState.current;
        if (!currentUser) throw new Error("No user");
        
        return new Promise<void>((resolve, reject) => {
            initializePayment({
                planId, 
                type, 
                user: currentUser,
                onSuccess: (paymentId) => {
                    setPendingTx(null); 
                    resolve();
                },
                onFailure: (err) => reject(err),
                onOrderCreated: (orderId) => {
                    // Save intent for recovery
                    setPendingTx({
                        orderId, 
                        planId, // Key change: store planId
                        type,
                        timestamp: Date.now()
                    });
                }
            });
        });
    }, []);

    const cancelSubscription = useCallback(async () => {
        const manageSubFn = httpsCallable(functions, 'manageSubscription');
        await manageSubFn({ action: 'cancel' });
    }, []);

    const resumeSubscription = useCallback(async () => {
        const manageSubFn = httpsCallable(functions, 'manageSubscription');
        await manageSubFn({ action: 'resume' });
    }, []);

    const checkPaymentStatus = useCallback(async () => {
        if (!pendingTx) return false;

        try {
            const checkFn = httpsCallable(functions, 'checkOrderStatus');
            // Send Plan ID to server for verification
            const result = await checkFn({ 
                orderId: pendingTx.orderId, 
                planId: pendingTx.planId, 
                type: pendingTx.type 
            });
            
            const data = result.data as any;
            if (data.success) {
                setPendingTx(null); 
                return true;
            }
            return false;
        } catch (e) {
            console.error(e);
            return false;
        }
    }, [pendingTx]);

    const clearPendingOrder = () => setPendingTx(null);

    return (
        <AuthContext.Provider value={{ 
            user, login, loginWithEmail, registerWithEmail, logout,
            consumeSwarna, refundSwarna, buyCredits, cancelSubscription, resumeSubscription, checkPaymentStatus,
            transactions, authError, setAuthError,
            resendVerification, resetPassword, loginWithGoogle,
            isInitialized, isGoogleClientConfigured, canGenerate: () => true, isCloudConnected, 
            pendingTx, clearPendingOrder
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};